import { ref, computed } from "vue";
import { cartAPI, productsAPI, quotesAPI } from "@/api";

export function useB2BPricing() {
  const loading = ref(false);
  const error = ref("");

  // Calculate bulk discount for non-verified customers
  const calculateBulkDiscount = (
    quantity,
    isVerifiedCustomer,
    originalPrice
  ) => {
    if (isVerifiedCustomer || quantity < 10) {
      return 0;
    }

    // Max 15% discount for non-verified customers buying 10+ pieces
    const maxDiscount = 0.15;
    return maxDiscount; // Fixed 15% for 10+ items
  };

  // Get effective price for user (considering custom pricing and bulk discounts)
  const getEffectivePrice = async (productId, quantity = 1) => {
    try {
      const response = await productsAPI.getProductWithCustomPricing(
        productId,
        { quantity }
      );
      return response.data;
    } catch (err) {
      console.error("Failed to get effective price:", err);
      throw err;
    }
  };

  // Create quote from current cart
  const createQuote = async (quoteData) => {
    loading.value = true;
    error.value = "";

    try {
      const response = await cartAPI.createQuoteFromCart(quoteData);
      return response.data;
    } catch (err) {
      error.value =
        err.response?.data?.message ||
        "Nastala chyba pri vytváraní cenovej ponuky";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Convert quote to order
  const convertQuoteToOrder = async (quoteId, orderData) => {
    loading.value = true;
    error.value = "";

    try {
      const response = await quotesAPI.convertToOrder(quoteId, orderData);
      return response.data;
    } catch (err) {
      error.value =
        err.response?.data?.message ||
        "Nastala chyba pri konverzii na objednávku";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Get cart with B2B pricing applied
  const getCartWithPricing = async () => {
    loading.value = true;
    error.value = "";

    try {
      const response = await cartAPI.getCartWithPricing();
      return response.data;
    } catch (err) {
      error.value =
        err.response?.data?.message || "Nastala chyba pri načítavaní košíka";
      throw err;
    } finally {
      loading.value = false;
    }
  };

  // Check if B2B rolling minimum is met considering current period spend and this cart total
  const checkMinimumOrderRequirements = (user, cartTotal = 0) => {
    // Non-B2B users: no minimum enforcement
    if (!user || user.account_type !== "company") {
      return { meets: true, required: 0, missing: 0 };
    }

    // Fallback minimums in case backend fields are absent
    const fallbackMinimums = { b2b_s: 350, b2b_m: 1500, b2b_l: 5000 };

    const required =
      typeof user.b2b_minimum_required === "number"
        ? user.b2b_minimum_required
        : fallbackMinimums[user.company_account_type] || 0;

    const spend =
      typeof user.b2b_spend_current_period === "number"
        ? user.b2b_spend_current_period
        : 0;
    const meetsAlready =
      typeof user.b2b_has_met_minimum === "boolean"
        ? user.b2b_has_met_minimum
        : spend >= required;

    const projected = spend + (Number(cartTotal) || 0);
    const meetsWithOrder = projected >= required;

    return {
      meets: meetsAlready || meetsWithOrder,
      required,
      spend,
      projected,
      missing_now: Math.max(0, required - spend),
      missing: Math.max(0, required - projected),
      period_start: user.b2b_period_start || null,
      period_end: user.b2b_period_end || null,
    };
  };

  // Get B2B account type display name
  const getAccountTypeDisplayName = (accountType) => {
    const names = {
      b2b_s: "B2B S (30 dní, min. 350€)",
      b2b_m: "B2B M (90 dní, min. 1500€)",
      b2b_l: "B2B L (180 dní, min. 5000€)",
    };
    return names[accountType] || accountType;
  };

  // Format price with currency
  const formatPrice = (price) => {
    return new Intl.NumberFormat("sk-SK", {
      style: "currency",
      currency: "EUR",
    }).format(price);
  };

  return {
    loading,
    error,
    calculateBulkDiscount,
    getEffectivePrice,
    createQuote,
    convertQuoteToOrder,
    getCartWithPricing,
    checkMinimumOrderRequirements,
    getAccountTypeDisplayName,
    formatPrice,
  };
}
